import { pdf } from "@react-pdf/renderer";
import dayjs from "dayjs";
import type { Folder } from "~/api/model";
import logo3xPng from "~/assets/Logo3x.png";
import { downloadBlob } from "~/helpers";
import { getSafeEmailBodyLimit } from "~/utils/browserDetection";
import logger from "~/utils/logger";
import { downloadMarkdownAsDocx } from "./markdown-converters";
import { createPdfDocumentFromMarkdown } from "./pdf-markdown-converter";

type PdfCaseInfo = {
  caseName: string;
  dateOfBirth: string;
  dateOfLoss: string;
  claimNumber: string;
};

type PdfDocumentOptions = {
  title: string;
  includeTitle?: boolean;
  titleAlignment?: "left" | "center";
  showHeader?: boolean;
  caseInfo?: PdfCaseInfo;
  includeFooter?: boolean;
  includeLogo?: boolean;
  logoSrc?: string;
  fontFamily?: string;
  lineSpacing?: string;
};

export const downloadSummaryAsDocx = async (
  content: string,
  fileTitle: string,
  title?: string,
  fontFamily?: string,
  lineSpacing?: string
) => {
  await downloadMarkdownAsDocx(
    content,
    fileTitle,
    title,
    fontFamily,
    lineSpacing
  );
};

/**
 * Convert an image URL to a data URL (base64)
 * This is needed because @react-pdf/renderer doesn't support external URLs with query parameters
 */
const convertImageToDataUrl = async (url: string): Promise<string> => {
  try {
    // Fetch with credentials to handle S3 presigned URLs
    const response = await fetch(url, {
      mode: "cors",
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch image: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const contentType = blob.type?.toLowerCase() ?? "";

    logger.info("Image content type:", contentType);

    let isSvgUrl = false;
    try {
      const parsedUrl = new URL(url);
      isSvgUrl = parsedUrl.pathname.toLowerCase().endsWith(".svg");
    } catch {
      isSvgUrl = url.split("?")[0].toLowerCase().endsWith(".svg");
    }

    const isSvg = contentType.includes("image/svg") || isSvgUrl;

    // @react-pdf/renderer doesn't support SVG, so we need to convert it to PNG
    if (isSvg) {
      logger.info("Converting SVG to PNG for PDF compatibility");
      return await convertSvgToPng(blob);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        logger.info("Successfully converted image to data URL");
        resolve(result);
      };
      reader.onerror = () => {
        logger.error("FileReader error:", reader.error);
        reject(reader.error);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    logger.error("Failed to convert image to data URL:", error);
    throw error;
  }
};

/**
 * Convert SVG blob to PNG data URL
 * @react-pdf/renderer doesn't support SVG images
 */
export const convertSvgToPng = async (svgBlob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const svgText = event.target?.result as string;

        const img = new Image();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Failed to get canvas context");
        }

        const svgBlobForImage = new Blob([svgText], { type: "image/svg+xml" });
        const objectUrl = URL.createObjectURL(svgBlobForImage);

        const cleanup = () => {
          URL.revokeObjectURL(objectUrl);
        };

        img.onload = () => {
          try {
            const width = img.width || 200;
            const height = img.height || 100;

            canvas.width = width * 2;
            canvas.height = height * 2;

            ctx.fillStyle = "white"; // Avoid transparent background artifacts
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const pngDataUrl = canvas.toDataURL("image/png");
            logger.info("Successfully converted SVG to PNG");
            resolve(pngDataUrl);
          } catch (error) {
            logger.error("Error while rendering SVG onto canvas:", error);
            reject(error);
          } finally {
            cleanup();
          }
        };

        img.onerror = (error) => {
          logger.error("Failed to load SVG image:", error);
          cleanup();
          reject(new Error("Failed to load SVG image"));
        };

        img.src = objectUrl;
      } catch (error) {
        logger.error("Error in SVG to PNG conversion:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      logger.error("FileReader error while reading SVG:", reader.error);
      reject(reader.error);
    };

    reader.readAsText(svgBlob);
  });
};

export const generatePDFFile = async (
  content: string,
  title: string,
  options: Partial<Omit<PdfDocumentOptions, "title">> = {}
) => {
  const mergedOptions = {
    includeTitle: true,
    titleAlignment: "center" as const,
    ...options,
  };

  const shouldIncludeLogo = mergedOptions.includeLogo ?? true;

  let logoSrc = shouldIncludeLogo ? logo3xPng : undefined;

  // If a custom logoSrc is provided and it's an external URL, convert it to data URL
  if (
    shouldIncludeLogo &&
    mergedOptions.logoSrc &&
    mergedOptions.logoSrc !== logo3xPng
  ) {
    try {
      // If already a data URL, use it directly
      if (mergedOptions.logoSrc.startsWith("data:")) {
        logoSrc = mergedOptions.logoSrc;
      } else {
        // Convert external URL to base64 data URL
        logger.info(
          "Converting logo URL to data URL in generatePDFFile:",
          mergedOptions.logoSrc
        );
        logoSrc = await convertImageToDataUrl(mergedOptions.logoSrc);
      }
    } catch (error) {
      logger.error(
        "Failed to load custom logo in generatePDFFile, using default:",
        error
      );
      logoSrc = logo3xPng;
    }
  } else if (shouldIncludeLogo && mergedOptions.logoSrc) {
    logoSrc = mergedOptions.logoSrc;
  }

  const pdfDoc = createPdfDocumentFromMarkdown(content, {
    title,
    ...mergedOptions,
    includeLogo: shouldIncludeLogo,
    logoSrc,
  });

  const blob = await pdf(pdfDoc).toBlob();
  return blob;
};

export const downloadSummaryAsPdf = async (
  content: string,
  fileTitle: string,
  title: string,
  caseInfo?: PdfCaseInfo,
  fontFamily?: string,
  lineSpacing?: string,
  logoUrl?: string | null,
  footnote?: string | null
) => {
  // Convert custom logo URL to data URL if provided, otherwise use default
  let logoSrc = logo3xPng;

  if (
    logoUrl &&
    logoUrl !== logo3xPng &&
    logoUrl !== "https://placehold.co/100"
  ) {
    try {
      if (logoUrl.startsWith("data:")) {
        logoSrc = logoUrl;
      } else {
        // Convert external URL to base64 data URL for @react-pdf/renderer
        logger.info("Converting logo URL to data URL:", logoUrl);
        logoSrc = await convertImageToDataUrl(logoUrl);
      }
    } catch (error) {
      logger.error("Failed to load custom logo, using default:", error);
      logoSrc = logo3xPng;
    }
  }

  const pdfDoc = createPdfDocumentFromMarkdown(content, {
    title,
    includeTitle: true,
    titleAlignment: "left",
    showHeader: Boolean(caseInfo),
    caseInfo,
    includeFooter: true,
    includeLogo: true,
    logoSrc,
    fontFamily,
    lineSpacing,
    footnote,
  });

  const blob = await pdf(pdfDoc).toBlob();
  downloadBlob(blob, `${fileTitle}.pdf`);
};

export const sendSummaryWithEmail = (content: string, title: string) => {
  const subject = encodeURIComponent(title);

  let sanitizedContent = content
    .replace(/[\u2018\u2019]/g, "'") // Replace smart quotes
    .replace(/[\u201C\u201D]/g, '"') // Replace smart double quotes
    .replace(/[\u2013\u2014]/g, "-") // Replace em/en dashes
    .replace(/[\u00B0]/g, " degrees") // Replace degree symbol
    .replace(/[\u2192]/g, "->") // Replace right arrow
    .replace(/[\u2191]/g, "^") // Replace up arrow
    .replace(/[\u2264]/g, "<=") // Replace less than or equal
    .replace(/[^\x00-\x7F]/g, "?"); // Replace remaining non-ASCII characters

  let body = encodeURIComponent(sanitizedContent);

  const maxLength = getSafeEmailBodyLimit();
  if (body.length > maxLength) {
    const truncateAt = Math.floor(maxLength * 0.8); // Leave buffer for encoding
    body = body.substring(0, truncateAt);
    body += encodeURIComponent(
      "\n\n[Content truncated due to length limitations. Please refer to the original document for complete information.]"
    );
  }

  const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
  window.open(mailtoLink, "_blank");
};

export function downloadStringAsFile(content: string, fileTitle: string) {
  const blob = new Blob([content], { type: "text/plain" });
  downloadBlob(blob, `${fileTitle}.txt`);
}

export const getFileTitle = (title: string) => {
  if (title.includes(".")) {
    return title.split(".")[0];
  }
  return title;
};

export function formatCaseInfo(folder: Folder) {
  return {
    caseName: folder.name,
    dateOfBirth: dayjs(folder.dateOfBirth).format("MM/DD/YYYY"),
    dateOfLoss: dayjs(folder.dateOfLoss).format("MM/DD/YYYY"),
    claimNumber: folder.claimNumber,
  };
}

/**
 * Removes the Sources section from the content for exports.
 * The Sources section is appended by the AI API and contains clickable page references.
 * This function handles multiple formats:
 * 1. "Sources: [1], [2], [3]..." format (with "Sources:" label and commas)
 * 2. "Sources: 1 2 3" format (with "Sources:" label, space-separated)
 * 3. Inline references embedded directly in text (without "Sources:" label)
 * The class attribute may contain additional classes and appear in any position.
 * All page-reference anchor tags should be excluded from exports (plain text, docx, pdf, email).
 */
export function stripSourcesFromContent(content: string): string {
  const result = content.replace(
    /<span\s+class\s*=\s*["']sources-references["']>.*?<\/span>/gs,
    ""
  );
  return result.replace(/\n{3,}/g, "\n\n").trim();
}
